import React, { useId, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { OpenInNew } from '@mui/icons-material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Button, ChevronDown, ChevronUp, NewBadge } from '../../uikit';
import { ArticleSummary, RelatedDocument } from '../../types';
import { buildSummarySections, SummarySection } from './summarySections';

// COM-641: rebuilt on the kit (Figma 2871:60125 - a raster mock, so only its
// visual language is taken: large title, NEW badge, dark "Open source" pill,
// the PDF-export icon button beside it, date right-aligned, a divider, then
// collapsible white section cards). COM-638's extraction comment used to say
// this component's rendering had to stay byte-identical to the pre-redesign
// dialog it was lifted from - that constraint was scoped to COM-638 only and
// is lifted by this ticket, which is why every line below is new.
//
// The panel no longer renders its own close button: it used to sit
// `position: absolute` in a DialogTitle and collide with the kit `SidePanel`
// drawer's own close IconButton once AllArticlesPage.tsx started rendering
// this inside a `SidePanel` (both shared `aria-label="close"`, ambiguous to
// Playwright's role locator - see all-articles.spec.ts). Dropping the
// internal button removes the collision at the source instead of papering
// over it with more positioning; `onClose` is no longer a prop this
// component needs.
interface ArticleSummaryPanelProps {
  summary: ArticleSummary | null;
  loading: boolean;
  articleTitle: string;
  articleUrl: string;
  /** whether the article was unread when its summary was opened - a snapshot
   * the caller takes at open time (AllArticlesPage.tsx's `activeArticle`),
   * not a live subscription, so the badge doesn't disappear mid-read when
   * the read/unread flip (COM-638) clears the card's own badge. */
  isNew?: boolean;
  onExportPdf: () => void;
}

// Truncation thresholds for the "Read more" affordance Figma draws on long
// section content. Character/item counts, not measured line overflow (no
// DOM measurement here) - simple, deterministic, and good enough for the
// visual language there's nothing more precise to match against.
const PARAGRAPH_TRUNCATE_LENGTH = 220;
const LIST_TRUNCATE_COUNT = 4;

function SourceLinkButton({ articleUrl }: { articleUrl: string }) {
  if (!articleUrl) return null;
  return (
    <Button variant="primary" startIcon={<OpenInNew fontSize="small" />} onClick={() => window.open(articleUrl, '_blank')}>
      Open source
    </Button>
  );
}

function ExportPdfButton({ onExportPdf }: { onExportPdf: () => void }) {
  return (
    <IconButton
      aria-label="export-pdf"
      onClick={onExportPdf}
      sx={{ border: '1px solid #DEDEDE', backgroundColor: '#FFFFFF', color: '#0E0E0E' }}
    >
      <PictureAsPdfIcon fontSize="small" />
    </IconButton>
  );
}

/** One collapsible white section card. A single `ButtonBase` owns the
 * whole header row (heading + chevron) - there used to be a second,
 * competing `role="button"` on the outer row wrapping a focusable
 * `IconButton`, which was both unreachable by keyboard (no `tabIndex`) and
 * invalid ARIA (interactive nested inside interactive; it only "worked"
 * because the inner button's click bubbled up). One real button now owns
 * the toggle, with `aria-expanded`/`aria-controls` wired to the content
 * panel below so a screen reader announces collapsed/expanded, and the
 * chevron marked `aria-hidden` since it's decoration next to a labelled
 * control (the heading text is the button's accessible name).
 *
 * "Read more" (only rendered when the content is actually long enough to
 * truncate) reveals the rest in place, one-directional, mirroring Figma's
 * card style rather than a generic accordion. */
function SectionCard({ section }: { section: SummarySection }) {
  const [expanded, setExpanded] = useState(section.defaultExpanded);
  const [readMore, setReadMore] = useState(false);
  const contentId = useId();

  return (
    <Box
      sx={{
        border: '1px solid #DEDEDE',
        borderRadius: '8px',
        backgroundColor: '#FFFFFF',
        p: 2.5,
      }}
    >
      <ButtonBase
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-controls={contentId}
        sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 1, textAlign: 'left' }}
      >
        <Typography variant="h5">{section.heading}</Typography>
        <Box component="span" aria-hidden sx={{ display: 'inline-flex' }}>
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </Box>
      </ButtonBase>
      {/* Always rendered (never `{expanded && ...}`) and toggled with the
          native `hidden` attribute instead: `aria-controls={contentId}` on
          the header button above is only honest if an element with that id
          exists in the DOM regardless of collapsed state - six of eight
          sections start collapsed, so unmounting this would leave the
          attribute dangling at nothing most of the time. */}
      <Box id={contentId} hidden={!expanded} sx={{ mt: 1.5 }}>
        {section.isEmpty ? (
          <Typography fontSize={14} color="text.secondary">
            Not specified
          </Typography>
        ) : (
          <SectionContent section={section} readMore={readMore} onReadMore={() => setReadMore(true)} />
        )}
      </Box>
    </Box>
  );
}

function SectionContent({
  section,
  readMore,
  onReadMore,
}: {
  section: SummarySection;
  readMore: boolean;
  onReadMore: () => void;
}) {
  if (section.kind === 'paragraph') {
    const truncated = !readMore && section.value.length > PARAGRAPH_TRUNCATE_LENGTH;
    const text = truncated ? `${section.value.slice(0, PARAGRAPH_TRUNCATE_LENGTH).trimEnd()}…` : section.value;
    return (
      <>
        <Typography fontSize={14}>{text}</Typography>
        {truncated && <ReadMoreLink onClick={onReadMore} />}
      </>
    );
  }

  const visibleCount = readMore ? section.value.length : LIST_TRUNCATE_COUNT;
  const truncated = !readMore && section.value.length > LIST_TRUNCATE_COUNT;

  if (section.kind === 'list') {
    return (
      <>
        <Box component="ul" sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {section.value.slice(0, visibleCount).map((item, index) => (
            <Typography key={index} component="li" fontSize={14}>
              {item}
            </Typography>
          ))}
        </Box>
        {truncated && <ReadMoreLink onClick={onReadMore} />}
      </>
    );
  }

  // 'links'
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {section.value.slice(0, visibleCount).map((doc: RelatedDocument, index: number) => (
          // A native <a>, not a Box+onClick: the latter is invisible to
          // keyboard/screen-reader users (no role, no tabIndex, no key
          // handling) - the exact defect ad70469 fixed for "Read more" and
          // the section header, reintroduced here for a different control.
          // `rel="noopener noreferrer"` also closes the tabnabbing hole a
          // bare `window.open(doc.url, '_blank')` left open: without it the
          // opened page keeps a `window.opener` handle back into this app,
          // and `doc.url` is model-supplied, not ours to trust.
          <Box
            key={index}
            component="a"
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'inherit',
              textDecoration: 'none',
              width: 'fit-content',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <Typography fontSize={14} color="text.blue" sx={{ wordBreak: 'break-word' }}>
              {doc.title}
            </Typography>
            <OpenInNew fontSize="small" sx={{ color: 'text.blue', width: 14, height: 14 }} />
          </Box>
        ))}
      </Box>
      {truncated && <ReadMoreLink onClick={onReadMore} />}
    </>
  );
}

// A real button, not a `Typography` with only `onClick` (the latter is
// invisible to keyboard/screen-reader users - it never receives focus and
// has no key handling, so "Read more" could never be activated without a
// mouse; that includes the two sections expanded by default, key points
// and actions required, i.e. the panel's highest-value content). `ButtonBase`
// renders a native `<button>` (focusable, Enter/Space handled for free)
// while leaving full control over styling, matching the kit's own
// precedent for a compact inline text link (SourcesFilter.tsx's
// "Select all"/"Deselect all").
function ReadMoreLink({ onClick }: { onClick: () => void }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 500,
        color: 'text.blue',
        mt: 0.5,
        p: 0,
        width: 'fit-content',
        justifyContent: 'flex-start',
      }}
    >
      Read more
    </ButtonBase>
  );
}

/**
 * The panel's no-summary state. Deliberately its own block (not a shared
 * "empty section" case within the AC's section list) so COM-944 can add
 * "content not locally verified" / "this article could not be read" as
 * siblings here without a rebuild - see the design spec's "Missing
 * summaries are COM-944's problem" section. No retry affordance: retrying a
 * fragile fetch is not a fix (decided with Artur, 2026-08-19).
 *
 * Message-only: the source link itself lives in the header row above (always
 * visible, summary or not), so this doesn't repeat an "Open source" button -
 * one visible source link per panel, not two.
 */
function NoSummaryState() {
  return (
    <Typography fontSize={14} color="text.secondary" sx={{ py: 2 }}>
      No summary was generated for this article.
    </Typography>
  );
}

const ArticleSummaryPanel: React.FC<ArticleSummaryPanelProps> = ({
  summary,
  loading,
  articleTitle,
  articleUrl,
  isNew = false,
  onExportPdf,
}) => {
  const sections = buildSummarySections(summary);

  return (
    // Keyed on `articleUrl`: remounts the whole subtree (and every
    // SectionCard's own expanded/readMore state) when a different article's
    // summary is opened, instead of a stale chevron state leaking across
    // articles - simpler than threading a reset effect through every card.
    <Box key={articleUrl} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="h3">{articleTitle || 'Article Summary'}</Typography>
        {isNew && <NewBadge />}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SourceLinkButton articleUrl={articleUrl} />
          <ExportPdfButton onExportPdf={onExportPdf} />
        </Box>
        {summary?.publicationDate && (
          <Typography fontSize={12} color="text.secondary">
            {summary.publicationDate}
          </Typography>
        )}
      </Box>

      <Divider />

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <CircularProgress size={20} />
          <Typography>Loading summary…</Typography>
        </Box>
      )}

      {!loading && sections === null && <NoSummaryState />}

      {!loading && sections !== null && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sections.map((section) => (
            <SectionCard key={section.key} section={section} />
          ))}
        </Box>
      )}

      <Paper sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.05)', fontStyle: 'italic' }} elevation={0}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Disclaimer:</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          The content on this page is provided for general informational purposes only. It is automatically generated by an AI summarization tool and may not encompass all material information contained in the original source. While we strive for accuracy, we do not warrant or guarantee its completeness, timeliness, or suitability for any particular purpose.
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 2 }}>Important:</Typography>
        {/* `component="div"`: `Typography variant="body2"` renders a `<p>`,
            and a `<ul>` inside a `<p>` is invalid DOM nesting - harmless
            visually, but it makes every component test log a jsdom
            `validateDOMNesting` warning. Carried over from the pre-redesign
            dialog; fixed here since the component tests now surface it. */}
        <Typography variant="body2" component="div" sx={{ mt: 1 }}>
          <ul>
            <li>This summary is not legal, financial, or professional advice.</li>
            <li>You should not act or rely solely on this information without first reviewing the full text of the relevant regulations and, if appropriate, seeking advice from qualified professionals.</li>
            <li>We expressly disclaim all liability for any loss or damage arising from reliance on this summary.</li>
          </ul>
        </Typography>
      </Paper>
    </Box>
  );
};

export default ArticleSummaryPanel;
